import React, { useState, useMemo } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  getDivisor,
  getFrequencyString,
  getIntervalString,
} from "../../../../utils/stripe-helpers";
import ToolTip from "../../../../parts/tooltip";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";

const PlansTable = ({
  isPress = false,
  plans,
  getPaymentLink,
  updatePlanStatus,
  preferPlan,
  planType = "press",
  gymLocations = [],
}) => {
  const activePlansLength = useMemo(() => {
    if (!plans?.length) return 0;
    return plans.filter((plan) => plan.active).length;
  }, [plans]);

  const StatusToggle = ({ published, id }) => {
    const [value, setValue] = useState(published);
    const [openedConfirmationModal, setOpenedConfirmationModal] =
      useState(false);

    return (
      <>
        <ToolTip
          id={`plan-status${id}`}
          body={published ? "Unpublish Plan" : "Publish Plan"}
        >
          <input
            type="checkbox"
            id={`switch-${id}`}
            name={`switch-${id}`}
            role="switch"
            onChange={() => {
              if (published && activePlansLength <= 2) {
                alert("You need at least 2 active plans");
                return;
              }
              setValue(!value);
              setOpenedConfirmationModal(!openedConfirmationModal);
            }}
            checked={!!value}
          />
        </ToolTip>
        <ConfirmationModal
          title={!value ? "Unpublish" : "Publish"}
          contentText={`Are you sure you want to ${
            !value ? "unpublish" : "publish"
          } this plan?`}
          cancelButtonText="Cancel"
          cancelButtonAction={() => {
            setOpenedConfirmationModal(false);
            setValue(!value);
          }}
          confirmButtonText={!value ? "Unpublish" : "Publish"}
          confirmButtonAction={() => updatePlanStatus(id, value)}
          isOpen={openedConfirmationModal}
          confirmSuccess={value}
        />
      </>
    );
  };

  return (
    <div className="plans-table">
      {plans && plans.length ? (
        <div className="s1 row table-head">
          <p className="s1 cell">Name</p>
          {isPress && <p className="s1 cell hide-md">Included</p>}
          <p className="s1 cell price">Price</p>
          {isPress && <p className="s1 cell hide-sm">Key Access</p>}
          <p className="s1 cell hide-sm">Recurring</p>
          <p className="s1 cell hide-sm">{isPress ? "User Access" : "Users"}</p>
          {planType === "push" && <p className="s1 cell hide-sm">Free trial</p>}
          {planType === "push" && (
            <p className="s1 cell hide-sm">Minimum term</p>
          )}
          <p className="s1 cell center narrow">Actions</p>
          <p className="s1 cell center">Status</p>
        </div>
      ) : null}

      {plans && plans.length ? (
        plans.map((plan) => {
          const {
            id,
            active,
            activeCount,
            currency,
            features,
            metadata = {},
            recurring,
            unit_amount,
            nickname,
            type,
            facilikey = [],
          } = plan;
          const {
            access = "",
            preferred = "false",
            trial_days,
            minimum_term,
          } = metadata;
          const isPreferred = preferred === "true";
          const symbol = getSymbolFromCurrency(currency);
          const divisor = getDivisor(currency);
          const price = unit_amount / divisor;
          const locations = gymLocations.filter((value) =>
            facilikey.includes(value.facilikey)
          );
          const isYearly = recurring?.interval === "year";
          return (
            <div className="row table-row" key={id}>
              {isPreferred && <div className="row-ribbon" />}
              <div className="cell">
                {planType === "push"
                  ? nickname || getIntervalString(recurring)
                  : nickname || "-"}
              </div>
              {planType === "press" && (
                <div className="cell hide-md">
                  {features?.length ? (
                    <ol>
                      {features.map((feature, i) => (
                        <li key={`${i}${feature}`}>{feature}</li>
                      ))}
                    </ol>
                  ) : (
                    "-"
                  )}
                </div>
              )}
              <div className="cell price">
                <p>
                  {symbol}
                  {price}{" "}
                </p>
                <span className="text-light">({currency.toUpperCase()})</span>
              </div>
              {isPress && (
                <div className="cell hide-sm">
                  {locations.length ? (
                    <ul>
                      {locations.map(({ gymName, gymLocation, gymContact }) => (
                        <li key={`${gymName}-${gymLocation}-${gymContact}`}>
                          {gymName}{" "}
                          <span className="text-light">({gymLocation})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "-"
                  )}
                </div>
              )}
              <div className="cell hide-sm">
                <div>{type === "recurring" ? "Yes" : "No"}</div>
                <div className="text-light">
                  ({getIntervalString(recurring)})
                </div>
              </div>
              {isPress ? (
                <div className="cell hide-sm">
                  <div>
                    {access
                      ? `${access[0].toUpperCase()}${access.slice(1)} Access`
                      : "-"}
                  </div>
                  <div className="text-light">
                    ({activeCount} {activeCount === 1 ? "user" : "users"})
                  </div>
                </div>
              ) : (
                <div className="cell hide-sm">{activeCount}</div>
              )}
              {planType === "push" && (
                <div className="cell hide-sm">
                  {!trial_days
                    ? "No"
                    : trial_days > 1
                    ? `${trial_days} days`
                    : `${trial_days} day`}
                </div>
              )}
              {planType === "push" && (
                <div className="cell hide-sm">
                  {!minimum_term
                    ? "No"
                    : `${minimum_term} ${getFrequencyString(
                        recurring,
                        minimum_term
                      )}`}
                </div>
              )}
              <div className="cell center narrow">
                <a
                  role="button"
                  data-button-type="primary"
                  href={`/actions/refresh?modal[type]=${
                    planType === "push" ? "plan-trial" : "create-plan"
                  }&type=${planType}&plan=${id}`}
                  className="edit icon-button"
                  sz="small"
                >
                  {planType === "push" ? "Free trial" : "Edit"}
                </a>
                <div className="icon-button-container">
                  <ToolTip id={`plan-details${id}`} body="View Plan Details">
                    <a
                      role="button"
                      href={`/actions/refresh?modal[type]=plan-details&plan=${id}&type=${planType}`}
                      data-button-type="secondary"
                      data-icon-only
                    >
                      <SvgIcon src="/img/new-icons/eye.svg" alt="View" />
                    </a>
                  </ToolTip>
                  <ToolTip
                    id={`payment-link${id}`}
                    body={active ? "Get Payment Link" : "Inactive plan"}
                  >
                    <a
                      href="#"
                      role="button"
                      data-button-type="secondary"
                      data-icon-only
                      onClick={(e) =>
                        getPaymentLink(
                          e,
                          isPress ? id : "",
                          isPress ? "gymmembership.app" : "programs.app"
                        )
                      }
                      style={active ? {} : { pointerEvents: "none" }}
                    >
                      <SvgIcon
                        src="/img/new-icons/link-white.svg"
                        alt="Payment link"
                      />
                    </a>
                  </ToolTip>
                  {!isPress && !isYearly && (
                    <ToolTip id={`minimum-term-${id}`} body={"Minimum term"}>
                      <a
                        href={`/actions/refresh?modal[type]=plan-minimum-term&type=${planType}&plan=${id}`}
                        role="button"
                        data-button-type="secondary"
                        data-icon-only
                      >
                        <SvgIcon src="/img/new-icons/minimum-term.svg" />
                      </a>
                    </ToolTip>
                  )}

                  {isPress && (
                    <ToolTip
                      id={`feature-plan${id}`}
                      body={
                        isPreferred ? "Remove Preferred" : "Mark as Preferred"
                      }
                    >
                      <a
                        role="button"
                        data-icon-only
                        data-button-type="secondary"
                        href="#"
                        onClick={(e) => preferPlan(e, id, preferred, plans)}
                      >
                        <SvgIcon
                          src={
                            isPreferred
                              ? "/img/new-icons/star-gradient.svg"
                              : "/img/new-icons/star.svg"
                          }
                          alt="Favorite"
                        />
                      </a>
                    </ToolTip>
                  )}
                </div>
              </div>
              <div className="cell center">
                <StatusToggle published={active} id={id} />
              </div>
            </div>
          );
        })
      ) : (
        <div className="no-data">No plans were found for this product</div>
      )}
    </div>
  );
};

export default PlansTable;
